import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

const STORAGE_KEY = 'ctime-theme';

/**
 * Get the initial theme, checking localStorage first
 * Default to 'light' if nothing stored
 */
function getInitialTheme() {
  // Check localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch (e) {
    // localStorage not available (e.g., private browsing)
    console.warn('localStorage not available for theme persistence');
  }
  
  // Default to light mode
  return 'light';
}

/**
 * Apply theme class to document element
 */
function applyTheme(theme) {
  const root = document.documentElement;
  
  if (theme === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    // Get initial theme and apply immediately to prevent flash
    const initial = getInitialTheme();
    // Apply theme immediately during SSR/hydration
    if (typeof window !== 'undefined') {
      applyTheme(initial);
    }
    return initial;
  });

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch (e) {
      console.warn('Could not persist theme to localStorage');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeProvider;
